import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { formatToCustomLocale } from "../../../../utilities";
import { Container, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import * as PIXI from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  setCounterAnimationStarted,
  setCounterAnimationFinished,
} from "../../../../State/actions";
import {
  selectCurrentRound,
  selectCurrency,
  selectIsSpinning,
  selectCounterAnimationStarted,
  selectExpandingWildsOccurred,
} from "../../../../State/selectors";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { FEATURES_CONFIG } from "../../../../featuresConfig";

/**
 * PopiWinDisplay - Simple win amount display component
 *
 * Displays win amounts immediately without animations.
 * Shows win for 2 seconds, then signals completion.
 *
 * @param {Object} toolbar - Toolbar reference for positioning
 */
const WinDisplay = ({ toolbar }) => {
  const dispatch = useDispatch();
  const currentRound = useSelector(selectCurrentRound);
  const currency = useSelector(selectCurrency);
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationStarted = useSelector(selectCounterAnimationStarted);
  const expandingWildsOccurred = useSelector(selectExpandingWildsOccurred);
  const { device, orientation, getResponsiveConfig, uiSymbolScale } =
    useDimensions();

  // Get button dimensions using the same system as PopiClock
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  // Get unified win configuration (font multiplier, x, y positions)
  const getWinConfig = () => {
    let fontMultiplier = 1.0;
    let x = 0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.05;
        x = toolbar.width / 2;
        y = -buttonConfig.height * 0.9;
      } else {
        fontMultiplier = 1.1;
        x = toolbar.width - buttonConfig.width * 8;
        y = toolbar.height / 2;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.7;
        x = toolbar.width / 2;
        y = -buttonConfig.height * 0.9;
      } else {
        fontMultiplier = 1.35;
        x = toolbar.width / 1.8;
        y = toolbar.height / 2;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      x = toolbar.width / 1.65;
      y = toolbar.height / 2;
    }

    return { fontMultiplier, x, y };
  };

  const winConfig = getWinConfig();
  const baseFontSize = 36;
  const responsiveFontSize = baseFontSize * winConfig.fontMultiplier;
  const FONT_SIZE = Math.floor(responsiveFontSize * uiSymbolScale);
  const LABEL_FONT_SIZE = Math.floor(FONT_SIZE * 0.95); // Slightly smaller for label

  // Show counter display and block user input until show all wins completes
  // Duration matches useWinController.js showAllWinningSymbolsOnce duration
  useEffect(() => {
    if (counterAnimationStarted) {
      // Use expandingWildsOccurred to determine which duration to use
      // This tracks whether expanding wilds ACTUALLY occurred in this spin, not just if the feature is enabled
      const displayDuration = expandingWildsOccurred
        ? FEATURES_CONFIG.EXPANDING_WILDS.ALL_WINS_DISPLAY_DURATION
        : FEATURES_CONFIG.SHOW_ALL_WINS.DISPLAY_DURATION;

      const timeout = setTimeout(() => {
        dispatch(setCounterAnimationFinished(true));
      }, displayDuration);

      return () => clearTimeout(timeout);
    }
  }, [counterAnimationStarted, expandingWildsOccurred, dispatch]);

  // Reset component state when new spin begins
  useEffect(() => {
    if (isSpinning) {
      dispatch(setCounterAnimationStarted(false));
      dispatch(setCounterAnimationFinished(false));
    }
  }, [isSpinning, dispatch]);

  // Don't render if no win or not started
  if (!counterAnimationStarted || !currentRound?.win) {
    return null;
  }

  // Format display values
  const winText = formatToCustomLocale(currentRound.win);
  const currencyText = ` ${currency}`;
  const winLabelText = "Win: ";

  // Win label text styling (white, static)
  const winLabelStyle = new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: LABEL_FONT_SIZE,
    fontWeight: "400",
    fill: "#ffffff",
    align: "center",
    ...TEXT_STYLE_CONFIG.OUTLINE,
  });

  // Win amount styling (golden color for emphasis)
  const winStyle = new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: FONT_SIZE,
    fontWeight: "700",
    fill: "#ffbf11",
    align: "center",
    ...TEXT_STYLE_CONFIG.OUTLINE,
  });

  // Currency text styling (golden for emphasis)
  const currencyStyle = new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: FONT_SIZE,
    fontWeight: "700",
    fill: "#ffbf11",
    align: "center",
    ...TEXT_STYLE_CONFIG.OUTLINE,
  });

  // Calculate text dimensions for proper positioning
  const winLabelMetrics = PIXI.TextMetrics.measureText(
    winLabelText,
    winLabelStyle
  );
  const winTextMetrics = PIXI.TextMetrics.measureText(winText, winStyle);
  const currencyTextMetrics = PIXI.TextMetrics.measureText(
    currencyText,
    currencyStyle
  );
  const totalTextWidth =
    winLabelMetrics.width + winTextMetrics.width + currencyTextMetrics.width;

  return (
    <Container
      name="win_display"
      x={winConfig.x}
      y={winConfig.y}
      zIndex={4}
      visible={counterAnimationStarted ? true : false}
    >
      {/* Win label text (white, static) */}
      <Text
        key={winLabelText}
        text={winLabelText}
        style={winLabelStyle}
        anchor={0.5}
        x={-totalTextWidth / 2 + winLabelMetrics.width / 2}
      />
      {/* Win amount text (golden, animated counter) */}
      <Text
        key={winText}
        text={winText}
        style={winStyle}
        anchor={0.5}
        x={
          -totalTextWidth / 2 + winLabelMetrics.width + winTextMetrics.width / 2
        }
      />
      {/* Currency text (golden, static) */}
      <Text
        key={currencyText}
        text={currencyText}
        style={currencyStyle}
        anchor={0.5}
        x={totalTextWidth / 2 - currencyTextMetrics.width / 2}
      />
    </Container>
  );
};

export default WinDisplay;
